import React from 'react';
import { HiOutlinePlus, HiOutlineViewList, HiOutlineX } from 'react-icons/hi';
import SessionsList from './sessions-list';
import UserProfile from './user-profile';

/**
 * Left rail: brand, conversation actions, history, and the guest chip.
 *
 * On desktop it is a static column; below `lg` it becomes an overlay drawer,
 * since the chat column needs the full width on a phone.
 */
export default function Sidebar({
  open,
  onClose,
  onToggle,
  title,
  logo,
  sessions,
  selectedSessionId,
  onSelectSession,
  onNewChat,
  onRefreshSessions,
  loading,
  unauthorized,
}) {
  return (
    <>
      {/* Scrim - mobile only, closes the drawer */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/*
        Two behaviours from one element:

        - below `lg` it is an overlay drawer, sliding in over the transcript;
        - at `lg` and up it is a column in the flex row, and collapsing it
          animates its width to zero so the transcript reclaims the space.

        The width (not the transform) is what animates on desktop - a
        translated sidebar would slide away but leave its 260px gap behind.
      */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col overflow-hidden border-r
          border-hairline bg-surface transition-transform duration-200
          lg:static lg:z-auto lg:translate-x-0 lg:transition-[width]
          ${open ? 'translate-x-0 lg:w-[260px]' : '-translate-x-full lg:w-0 lg:border-r-0'}`}
      >
        {/* Fixed-width panel: while the aside animates to w-0 the contents keep
            their natural width and clip, instead of reflowing on every frame. */}
        <div className="flex h-full w-[260px] flex-none flex-col">
        <div className="flex h-12 flex-none items-center justify-between px-3">
          <div className="flex min-w-0 items-center gap-2">
            {logo && (
              <img src={logo} alt="" className="h-5 w-5 flex-none rounded object-contain" />
            )}
            <span className="truncate text-[14px] font-semibold tracking-tight text-ink">
              {title}
            </span>
          </div>

          <button
            type="button"
            onClick={onToggle}
            aria-label="Close sidebar"
            className="rounded p-1 text-ink-faint transition-colors hover:bg-elevated hover:text-ink"
          >
            <HiOutlineX size={15} className="lg:hidden" />
            <HiOutlineViewList size={15} className="hidden lg:block" />
          </button>
        </div>

        <div className="flex-none px-2 pb-1">
          <button
            type="button"
            onClick={onNewChat}
            title="Start a new conversation"
            className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-ink
              transition-colors hover:bg-elevated"
          >
            <HiOutlinePlus size={15} className="flex-none text-ink-muted" />
            New chat
          </button>
        </div>

        <SessionsList
          sessions={sessions}
          selectedSessionId={selectedSessionId}
          onSelectSession={onSelectSession}
          onRefresh={onRefreshSessions}
          loading={loading}
          unauthorized={unauthorized}
        />

        <UserProfile />
        </div>
      </aside>
    </>
  );
}
